import React, { memo, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import styles from './styles.module.css';
import {
  Button,
  Input,
  SearchableSelect,
  Toggle,
  Dropdown,
  Loading,
} from '../../../../../common/ui';
import { getCategories } from '../../../categories/actions';
import {
  findProductForUpdate,
  findVariantProductsForUpdate,
  updateProductWithVariants,
} from '../../actions';
import { showAlert } from '../../../../../common/actions';
import { useNavigate, useParams } from 'react-router-dom';
import ProductVariantsTable from './ProductVariantsTable';
import AddUpdateVariantPopup from './AddUpdateVariantPopup';
import ImageUpload from '../AddNewProduct/ImageUpload';
import ModifierGroupAttach from '../ModifierGroupAttach';
import { fetchIsModifierCompany } from '../../../../reports/billHistory/utils/companyType';
import { fetchGroupsForProduct } from '../../../modifiers/actions';

const API_BASE = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5003/').replace(/\/$/, '');

const buildImageProxyUrl = (imagePath) =>
  `${API_BASE}/api/dashboard/products/image?key=${encodeURIComponent(imagePath)}`;

const typeOptions = [
  { value: 'STOCK', label: 'Stock' },
  { value: 'NON_STOCK', label: 'Non-Stock' },
];

const initialProduct = {
  name: '',
  code: '',
  barcode: '',
  isSerialCheck: false,
  type: 'STOCK',
  nonStockPrice: '',
  nonStockCost: '',
  nonStockProductAvailability: false,
  nonStockId: '',
  isVariantAvailable: false,
  variantsDetails: [],
  deleteVariants: [],
};

const UpdateProduct = memo(
  ({
    getCategories,
    categoriesState: { categoriesData },
    findProductForUpdate: fetchProduct,
    findVariantProductsForUpdate: fetchVariants,
    updateProductWithVariants: doUpdate,
    showAlert,
    auth: { companyId },
  }) => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [loadingProduct, setLoadingProduct] = useState(true);
    const [productFetched, setProductFetched] = useState(false);
    const [isSubmitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({
      name: '',
      category: '',
      price: '',
    });
    const [newProduct, setNewProduct] = useState(initialProduct);
    const [originalProductData, setOriginalProductData] = useState({
      ...initialProduct,
      variantsDetails: [],
    });
    const [categoryId, setCategoryId] = useState(null);
    const [isAddUpdateVariantPopupOpen, setIsAddUpdateVariantPopupOpen] =
      useState(false);
    const [selectedVariantIndex, setSelectedVariantIndex] = useState(-1);
    const [variantTableFocusIndex, setVariantTableFocusIndex] = useState(-1);
    const [productImages, setProductImages] = useState([]);
    const [originalProductImageIds, setOriginalProductImageIds] = useState([]);
    const [modifiersEnabled, setModifiersEnabled] = useState(false);
    const [modifierGroupIds, setModifierGroupIds] = useState([]);

    useEffect(() => {
      getCategories();
    }, [getCategories]);

    useEffect(() => {
      let cancelled = false;
      const load = async () => {
        const enabled = await fetchIsModifierCompany(companyId);
        if (!cancelled) setModifiersEnabled(enabled);
      };
      load();
      return () => {
        cancelled = true;
      };
    }, [companyId]);

    useEffect(() => {
      if (!id) return;
      let cancelled = false;
      setLoadingProduct(true);
      setProductFetched(false);
      (async () => {
        const data = await fetchProduct(id);
        if (cancelled) return;
        if (!data) {
          setLoadingProduct(false);
          setNewProduct((prev) => ({ ...prev, _notFound: true }));
          return;
        }
        const productPayload = {
          name: data.name ?? '',
          code: data.code ?? '',
          barcode: data.barcode ?? '',
          isSerialCheck:
            data.isSerialCheck === true ||
            data.isSerialCheck === 1 ||
            data.isSerialCheck === '1',
          type: data.productType ?? 'STOCK',
          nonStockPrice:
            data.nonStockPrice != null && data.nonStockPrice !== ''
              ? String(data.nonStockPrice)
              : '',
          nonStockCost:
            data.nonStockCost != null && data.nonStockCost !== ''
              ? String(data.nonStockCost)
              : '',
          nonStockProductAvailability:
            data.nonStockProductAvailability === true ||
            data.nonStockProductAvailability === '1' ||
            data.nonStockProductAvailability === 1,
          nonStockId: data.nonStockId ?? '',
          isVariantAvailable:
            data.isVariantAvailable === true ||
            data.isVariantAvailable === 1 ||
            data.isVariantAvailable === '1',
          variantsDetails: [],
          deleteVariants: [],
        };
        setNewProduct(productPayload);
        setOriginalProductData({
          ...productPayload,
          variantsDetails: [],
        });
        setCategoryId(data.categoryid ?? data.categoryId ?? null);

        const attachedIds = Array.isArray(data.modifierGroupIds)
          ? data.modifierGroupIds.map(String)
          : [];
        if (attachedIds.length > 0) {
          setModifierGroupIds(attachedIds);
        } else {
          try {
            const attached = await fetchGroupsForProduct(id);
            if (!cancelled && Array.isArray(attached)) {
              setModifierGroupIds(attached.map((g) => String(g.id)));
            }
          } catch (_) {
            setModifierGroupIds([]);
          }
        }

        // Load existing product images (non-variant products)
        if (!data.isVariantAvailable) {
          const existingImgs = (data.images || []).map((img) => ({
            dataUrl: buildImageProxyUrl(img.imagePath),
            existingId: img.id,
            existingPath: img.imagePath,
            convertedSize: 0,
          }));
          setProductImages(existingImgs);
          setOriginalProductImageIds((data.images || []).map((img) => img.id));
        }

        if (data.isVariantAvailable) {
          const variantDetails = await fetchVariants(id);
          if (!cancelled && Array.isArray(variantDetails)) {
            // Map server images into the same shape the ImageUpload component expects
            const variantDetailsWithImages = variantDetails.map((v) => ({
              ...v,
              images: (v.images || []).map((img) => ({
                dataUrl: buildImageProxyUrl(img.imagePath),
                existingId: img.id,
                existingPath: img.imagePath,
                convertedSize: 0,
              })),
            }));
            setNewProduct((prev) => ({
              ...prev,
              variantsDetails: variantDetailsWithImages,
            }));
            setOriginalProductData((prev) => ({
              ...prev,
              variantsDetails: variantDetailsWithImages,
            }));
          }
        }
        setProductFetched(true);
        setLoadingProduct(false);
      })();
      return () => {
        cancelled = true;
      };
    }, [id, fetchProduct, fetchVariants]);

    const onFieldChange = (field, value) => {
      setNewProduct((prev) => ({ ...prev, [field]: value }));
      if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
    };

    const onCategoryChange = (value) => {
      setCategoryId(value);
      if (errors.category) setErrors((prev) => ({ ...prev, category: '' }));
    };

    const validate = () => {
      const nameTrim = (newProduct.name ?? '').trim();
      const nameErr = !nameTrim ? 'Product name is required' : '';
      const categoryErr =
        categoryId == null || categoryId === ''
          ? 'Please select a category'
          : '';
      const priceErr =
        !newProduct.isVariantAvailable &&
        newProduct.type === 'NON_STOCK' &&
        (newProduct.nonStockPrice === '' || newProduct.nonStockPrice == null)
          ? 'Price is required'
          : '';
      setErrors({ name: nameErr, category: categoryErr, price: priceErr });
      if (
        newProduct.isVariantAvailable &&
        (newProduct.variantsDetails?.length ?? 0) === 0
      ) {
        showAlert({
          isAlertOpen: true,
          alertType: 'danger',
          alertMessage: 'Please add at least one variant',
        });
        return false;
      }
      return !nameErr && !categoryErr && !priceErr;
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!validate()) return;
      setSubmitting(true);
      const nameTrim = (newProduct.name ?? '').trim();
      const payload = {
        id,
        name: nameTrim,
        code: newProduct.code ?? '',
        barcode: newProduct.barcode ?? '',
        categoryid: categoryId,
        isSerialCheck: newProduct.isSerialCheck,
        type: newProduct.type,
        isVariantAvailable: newProduct.isVariantAvailable,
      };
      const priceChanged =
        newProduct.nonStockPrice !== originalProductData.nonStockPrice ||
        newProduct.nonStockCost !== originalProductData.nonStockCost ||
        newProduct.nonStockProductAvailability !==
          originalProductData.nonStockProductAvailability;
      if (
        newProduct.type === 'NON_STOCK' &&
        !newProduct.isVariantAvailable &&
        priceChanged
      ) {
        payload.nonStockId = newProduct.nonStockId || undefined;
        payload.price =
          newProduct.nonStockPrice !== '' && newProduct.nonStockPrice != null
            ? parseFloat(newProduct.nonStockPrice)
            : 0;
        payload.cost =
          newProduct.nonStockCost !== '' && newProduct.nonStockCost != null
            ? parseFloat(newProduct.nonStockCost)
            : 0;
        payload.isAvailable = !!newProduct.nonStockProductAvailability;
      }
      if (newProduct.deleteVariants?.length > 0) {
        // deleteVariants is always built as [{ id: variantId }] in removeVariantItem — use it directly.
        payload.deleteVariants = newProduct.deleteVariants;
      }
      // Field-level equality check — avoids JSON.stringify key-order sensitivity.
      const variantDataEqual = (orig, curr) =>
        (orig.variantCode ?? '') === (curr.variantCode ?? '') &&
        (orig.variantBarcode ?? '') === (curr.variantBarcode ?? '') &&
        (orig.variantName ?? '') === (curr.variantName ?? '') &&
        !!orig.variantIsSerialCheck === !!curr.variantIsSerialCheck &&
        Number(orig.nonStockPrice ?? 0) === Number(curr.nonStockPrice ?? 0) &&
        Number(orig.nonStockCost ?? 0) === Number(curr.nonStockCost ?? 0) &&
        !!orig.nonStockProductAvailability === !!curr.nonStockProductAvailability;

      // O(1) lookup of original variants by DB id.
      const origVariantById = Object.fromEntries(
        (originalProductData.variantsDetails ?? [])
          .filter((v) => v.variantId != null)
          .map((v) => [v.variantId, v])
      );

      // Only include variants that actually need a DB write:
      //  - brand-new (no variantId yet)
      //  - new images were attached
      //  - any data field differs from the original snapshot
      // Removed-image ids are tracked in deleteVariantImageIds and sent separately,
      // so a variant whose only change was image removal does NOT need to be here.
      const changedVariants = (newProduct.variantsDetails ?? []).filter((v) => {
        if (v.variantId == null) return true;
        if ((v.images ?? []).some((img) => !img.existingId)) return true;
        const orig = origVariantById[v.variantId];
        if (!orig) return true;
        return !variantDataEqual(orig, v);
      });

      if (changedVariants.length > 0) {
        payload.variantsDetails = changedVariants.map((v) => {
          const row = {
            variantCode: v.variantCode ?? '',
            variantBarcode: v.variantBarcode ?? '',
            variantName: v.variantName ?? '',
            productType: newProduct.type,
            variantIsSerialCheck: !!v.variantIsSerialCheck,
            nonStockPrice:
              v.nonStockPrice != null ? Number(v.nonStockPrice) : 0,
            nonStockCost: v.nonStockCost != null ? Number(v.nonStockCost) : 0,
            nonStockProductAvailability: !!v.nonStockProductAvailability,
            images: v.images ?? [],
          };
          if (v.variantId != null) row.variantId = v.variantId;
          if (v.nonStockId != null) row.nonStockId = v.nonStockId;
          return row;
        });
      }

      // Compute which existing parent images were removed (not present in current state)
      const currentExistingProductImageIds = productImages
        .filter((img) => img.existingId)
        .map((img) => img.existingId);
      const deleteProductImageIds = originalProductImageIds.filter(
        (oid) => !currentExistingProductImageIds.includes(oid)
      );

      // Pre-build a Set of every existing image id that is still present across
      // all current variants — O(total images) — so the outer loop becomes O(1) per image.
      const currentVariantImageIdSet = new Set(
        (newProduct.variantsDetails ?? []).flatMap((v) =>
          (v.images ?? []).map((img) => img.existingId).filter(Boolean)
        )
      );

      // Compute which existing variant images were removed per variant
      const deleteVariantImageIds = [];
      (originalProductData.variantsDetails ?? []).forEach((origVariant) => {
        (origVariant.images ?? []).forEach((origImg) => {
          if (!origImg.existingId) return;
          if (!currentVariantImageIdSet.has(origImg.existingId)) {
            deleteVariantImageIds.push(origImg.existingId);
          }
        });
      });

      payload.deleteImageIds = [...deleteProductImageIds, ...deleteVariantImageIds];
      payload.images = newProduct.isVariantAvailable ? [] : productImages;
      if (modifiersEnabled) {
        payload.modifierGroupIds = modifierGroupIds;
      }

      const success = await doUpdate(payload);
      setSubmitting(false);
      if (success) {
        showAlert({
          isAlertOpen: true,
          alertType: 'success',
          alertMessage: 'Product updated successfully.',
        });
        navigate('/inventory/products');
      } else {
        showAlert({
          isAlertOpen: true,
          alertType: 'danger',
          alertMessage: 'Something went wrong. Please try again.',
        });
      }
    };

    const removeVariantItem = (variant, index) => {
      setNewProduct((prev) => {
        const nextDelete = [...(prev.deleteVariants ?? [])];
        if (variant.variantId != null) {
          nextDelete.push({ id: variant.variantId });
        }
        return {
          ...prev,
          deleteVariants: nextDelete,
          variantsDetails: prev.variantsDetails.filter((_, i) => i !== index),
        };
      });
    };

    const onEditItem = (variant, index) => {
      setSelectedVariantIndex(index);
      setVariantTableFocusIndex(index);
      setIsAddUpdateVariantPopupOpen(true);
    };

    const handleCancel = () => navigate('/inventory/products');

    const productNotFound = !!newProduct._notFound;
    // productFetched is only set true after the API call returns valid data,
    // so this correctly handles products with an empty name or no category.
    const productLoaded = !loadingProduct && !productNotFound && productFetched;
    const categoryOptions = Array.isArray(categoriesData)
      ? categoriesData.map((c) => ({ id: c.id, name: c.name }))
      : [];

    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.titleSection}>
            <h1 className={styles.pageTitle}>Update Product</h1>
            <p className={styles.breadcrumb}>
              Inventory / Products / Update
            </p>
          </div>
        </div>
        <div className={styles.body}>
          <div className={styles.formCardWrap}>
            {loadingProduct && (
              <Loading variant="overlay" label="Loading product…" />
            )}
            {loadingProduct && (
              <div className={styles.formCard} aria-hidden />
            )}
            {productNotFound && (
              <div className={styles.formCard}>
                <p className={styles.notFoundMessage}>Product not found.</p>
                <div className={styles.buttonPanel}>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => navigate('/inventory/products')}
                  >
                    Back to products
                  </Button>
                </div>
              </div>
            )}
            {productLoaded && (
              <form
                className={styles.formCard}
                onSubmit={handleSubmit}
                noValidate
              >
                <div className={styles.formFields}>
                  <div className={styles.formRow}>
                    <div className={styles.fieldGroup}>
                      <Input
                        id="name"
                        label="Name"
                        type="text"
                        value={newProduct.name ?? ''}
                        onChange={(value) => onFieldChange('name', value)}
                        error={errors.name}
                        placeholder="Enter product name"
                      />
                    </div>
                    <div className={styles.fieldGroup}>
                      <Input
                        id="code"
                        label="Code"
                        type="text"
                        value={newProduct.code ?? ''}
                        onChange={(value) => onFieldChange('code', value)}
                      />
                    </div>
                  </div>
                  <div className={styles.formRow}>
                    <div className={styles.fieldGroup}>
                      <Input
                        id="barcode"
                        label="Barcode"
                        type="text"
                        value={newProduct.barcode ?? ''}
                        onChange={(value) => onFieldChange('barcode', value)}
                      />
                    </div>
                    <div className={styles.fieldGroup}>
                      <SearchableSelect
                        label="Category"
                        options={categoryOptions}
                        getOptionLabel={(o) => o.name}
                        getOptionValue={(o) => o.id}
                        value={categoryId}
                        onChange={onCategoryChange}
                        placeholder="Not selected"
                      />
                      {errors.category ? (
                        <span className={styles.fieldError}>
                          {errors.category}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  <div className={styles.divider} />

                  <div className={styles.formRow}>
                    <div className={styles.fieldGroup}>
                      <Toggle
                        label="Variants Available"
                        labelPlacement="start"
                        name="variantsAvailable"
                        checked={!!newProduct.isVariantAvailable}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          if (
                            !checked &&
                            (newProduct.variantsDetails?.length ?? 0) > 0
                          ) {
                            showAlert({
                              isAlertOpen: true,
                              alertType: 'warning',
                              alertMessage:
                                'Please remove all variants before disabling this option.',
                            });
                            return;
                          }
                          onFieldChange('isVariantAvailable', checked);
                          setProductImages([]);
                        }}
                      />
                    </div>
                    <div className={styles.fieldGroup} />
                  </div>

                  {!newProduct.isVariantAvailable && (
                    <>
                      <div className={styles.formRow}>
                        <div className={styles.fieldGroup}>
                          <Dropdown
                            label="Type"
                            options={typeOptions}
                            value={newProduct.type}
                            onChange={() => {}}
                            disabled
                          />
                        </div>
                        <div className={styles.fieldGroup} />
                      </div>
                      {newProduct.type === 'STOCK' && (
                        <div className={styles.formRow}>
                          <div className={styles.fieldGroup}>
                            <Toggle
                              label="Serial Number Validation"
                              labelPlacement="start"
                              name="serialCheck"
                              checked={!!newProduct.isSerialCheck}
                              onChange={(e) =>
                                onFieldChange(
                                  'isSerialCheck',
                                  e.target.checked
                                )
                              }
                            />
                          </div>
                          <div className={styles.fieldGroup} />
                        </div>
                      )}
                      {newProduct.type === 'NON_STOCK' && (
                        <>
                          <div className={styles.formRow}>
                            <div className={styles.fieldGroup}>
                              <Input
                                id="price"
                                label="Price"
                                type="text"
                                value={newProduct.nonStockPrice ?? ''}
                                onChange={(value) =>
                                  onFieldChange('nonStockPrice', value)
                                }
                                error={errors.price}
                                placeholder="0.00"
                              />
                            </div>
                            <div className={styles.fieldGroup}>
                              <Input
                                id="cost"
                                label="Cost"
                                type="text"
                                value={newProduct.nonStockCost ?? ''}
                                onChange={(value) =>
                                  onFieldChange('nonStockCost', value)
                                }
                                placeholder="0.00"
                              />
                            </div>
                          </div>
                          <div className={styles.formRow}>
                            <div className={styles.fieldGroup}>
                              <Toggle
                                label="Availability"
                                labelPlacement="start"
                                name="availability"
                                checked={!!newProduct.nonStockProductAvailability}
                                onChange={(e) =>
                                  onFieldChange(
                                    'nonStockProductAvailability',
                                    e.target.checked
                                  )
                                }
                              />
                            </div>
                            <div className={styles.fieldGroup} />
                          </div>
                        </>
                      )}
                    </>
                  )}

                  {newProduct.isVariantAvailable && (
                    <>
                      <div className={styles.formRow}>
                        <div className={styles.fieldGroup}>
                          <Dropdown
                            label="Type"
                            options={typeOptions}
                            value={newProduct.type}
                            onChange={() => {}}
                            disabled
                          />
                        </div>
                        <div className={styles.fieldGroup} />
                      </div>
                      <div className={styles.addVariantRow}>
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() => {
                            setSelectedVariantIndex(-1);
                            setIsAddUpdateVariantPopupOpen(true);
                          }}
                        >
                          Add Variant
                        </Button>
                      </div>
                      <ProductVariantsTable
                        rows={newProduct.variantsDetails}
                        variantTableFocusIndex={variantTableFocusIndex}
                        productType={newProduct.type}
                        removeVariantItem={removeVariantItem}
                        onEditItem={onEditItem}
                      />
                    </>
                  )}
                </div>

                {!newProduct.isVariantAvailable && (
                  <>
                    <div className={styles.divider} />
                    <ImageUpload
                      images={productImages}
                      onChange={setProductImages}
                    />
                  </>
                )}

                {modifiersEnabled && (
                  <>
                    <div className={styles.divider} />
                    <h3>Modifier groups</h3>
                    <ModifierGroupAttach
                      companyId={companyId}
                      selectedIds={modifierGroupIds}
                      onChange={setModifierGroupIds}
                    />
                  </>
                )}

                <div className={styles.buttonPanel}>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Saving…' : 'Update product'}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>

        <AddUpdateVariantPopup
          isOpen={isAddUpdateVariantPopupOpen}
          onClose={() => {
            setIsAddUpdateVariantPopupOpen(false);
            setSelectedVariantIndex(-1);
          }}
          newProduct={newProduct}
          setNewProduct={setNewProduct}
          selectedVariantIndex={selectedVariantIndex}
        />
      </div>
    );
  }
);

const mapStateToProps = (state) => ({
  categoriesState: state.categoriesState,
  auth: state.authState,
});

export default connect(mapStateToProps, {
  getCategories,
  findProductForUpdate,
  findVariantProductsForUpdate,
  updateProductWithVariants,
  showAlert,
})(UpdateProduct);
